import { Button, Dialog } from "@material-ui/core";
import AppBar from "components/app-bar";
import DeleteEventDialog from "components/delete-event-dialog";
import ErrorMessage from "components/error-message";
import SlideUpTransition from "components/slide-up-transition";
import {
  CancelButton,
  UpdateButton,
  CircularProgress,
  InformationMessage,
  DangerZoneSection,
  DangerZoneTitle,
  DangerZoneDescription,
  DeleteEventButton,
} from "containers/EditEvent/EditEvent.styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useRouteMatch } from "react-router";
import { TastingEvent } from "store/reducers/events/reducer";
import EventEditForm, {
  EventEditFormChangeHandler,
} from "../../components/event-edit-form";
import { UserInfo } from "store/reducer";
import { events, RootState, StoreError } from "../../store";

const EditEvent = () => {
  const { id } = useParams<{ id: string }>();
  const { url: baseURL } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector<RootState, UserInfo | null>((s) => s?.auth?.user);
  const updating = useSelector<RootState, boolean>(
    (s) => s?.events?.update.updating
  );
  const updated = useSelector<RootState, TastingEvent | null>(
    (s) => s?.events?.update.updated
  );
  const error = useSelector<RootState, StoreError | null>(
    (s) => s?.events?.update.error
  );
  const loading = useSelector<RootState, boolean>(
    (s) => s?.events?.itemsLoading[id]
  );
  const loadError = useSelector<RootState, StoreError | undefined>(
    (s) => s?.events?.itemsError?.[id]
  );
  const item = useSelector<RootState, TastingEvent | undefined>((s) =>
    s?.events?.items?.find((i) => i.id === id)
  );
  const deleting = useSelector<RootState, boolean>(
    (s) => s?.events?.delete?.deleting?.[id] ?? false
  );
  const deleted = useSelector<RootState, string | null>(
    (s) => s?.events?.delete?.deleted ?? null
  );
  const deleteError = useSelector<RootState, StoreError | null>(
    (s) => s?.events?.delete?.error ?? null
  );
  const [event, setEvent] = useState<TastingEvent | undefined>(item);
  const draft = useRef({ id, edited: false });
  const [open, setOpen] = useState<boolean>(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const backURL = baseURL.replace("/edit", "");
  const isOwner = !!user?.email && !!item?.owner && user.email === item.owner;

  useEffect(() => {
    dispatch(events.loadItem(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (draft.current.id !== id) draft.current = { id, edited: false };
    // Fresh data can update an untouched form, but must not erase edits.
    if (!draft.current.edited) setEvent(item);
  }, [id, item]);

  const handleCancel = useCallback(() => {
    dispatch(events.resetUpdate());
    setOpen(false);
  }, [dispatch]);

  useEffect(() => {
    if (updated) {
      handleCancel();
    }
  }, [updated, history, dispatch, handleCancel]);

  // Once the event is deleted, close the dialog and return to the events list.
  useEffect(() => {
    if (deleted === id) {
      setDeleteDialogOpen(false);
      dispatch(events.resetDelete());
      setOpen(false);
      history.push("/");
    }
  }, [deleted, id, dispatch, history]);

  const handleSave = () => {
    if (
      event &&
      window.confirm("All answers and results will be cleared. Are you sure?")
    ) {
      dispatch(events.update(event));
    }
  };

  const handleExit = () => {
    history.push(backURL);
  };

  const handleEventChange: EventEditFormChangeHandler = (event) => {
    draft.current.edited = true;
    setEvent(event);
  };

  const handleOpenDeleteDialog = () => {
    dispatch(events.resetDelete());
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    dispatch(events.resetDelete());
  };

  const handleConfirmDelete = () => {
    dispatch(events.deleteEvent(id));
  };

  return (
    <div data-testid="edit-event">
      <Dialog
        fullScreen
        open={open}
        onClose={handleCancel}
        TransitionProps={{ onExited: handleExit }}
        TransitionComponent={SlideUpTransition}
      >
        <AppBar
          renderLeftComponent={() => <CancelButton onClick={handleCancel} />}
          title="Edit Event"
          renderRightComponent={() => (
            <UpdateButton isSaving={updating} onClick={handleSave} />
          )}
        />
        <InformationMessage>
          Warning: Editing an event clears all answers and results.
        </InformationMessage>
        {error && <ErrorMessage error={error} />}
        {deleteError && <ErrorMessage error={deleteError} />}
        {loadError && (
          <ErrorMessage error={loadError}>
            <Button color="inherit" onClick={() => dispatch(events.loadItem(id))}>
              Retry
            </Button>
          </ErrorMessage>
        )}
        {loading && !event ? (
          <CircularProgress />
        ) : (
          <>
            {event && (
              <EventEditForm event={event} onChange={handleEventChange} />
            )}
            {isOwner && (
              <DangerZoneSection data-testid="danger-zone">
                <DangerZoneTitle>Danger Zone</DangerZoneTitle>
                <DangerZoneDescription>
                  Deleting this event permanently removes all rounds, answers,
                  and results. This action cannot be undone.
                </DangerZoneDescription>
                <DeleteEventButton
                  data-testid="delete-event-button"
                  deleting={deleting}
                  onClick={handleOpenDeleteDialog}
                />
              </DangerZoneSection>
            )}
          </>
        )}
      </Dialog>
      <DeleteEventDialog
        open={deleteDialogOpen}
        eventName={item?.name ?? ""}
        deleting={deleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
export default EditEvent;
